import { useMediaQuery } from '@uhg-abyss/web/hooks/useMediaQuery';
import { styled } from '@uhg-abyss/web/tools/styled';
import { Button } from '@uhg-abyss/web/ui/Button';
import { Carousel, Slide } from '@uhg-abyss/web/ui/Carousel';
import { Heading } from '@uhg-abyss/web/ui/Heading';
import { Layout } from '@uhg-abyss/web/ui/Layout';
import { Text } from '@uhg-abyss/web/ui/Text';
import debounce from 'lodash/debounce';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useShallow } from 'zustand/react/shallow';

import { useAdobePageTrackEvent } from '../../hooks/adobeHook/useAdobePageTrackEvent';
import { useChipStore } from '../../store/useChipStore';
import { ChipState } from '../../store/useChipStore/chipStore';
import { adobeLinkTrackEvent } from '../AdobeTagging/adobeLinkTrackEvent';
import { benefitsCarouselScreen, phoneOnly } from '../ConstantsStyles';
import { ContentWrapper } from '../ContentWrapper';
import {
  carouselStyle,
  slideContainerStyle,
} from '../MyBenefits/MyBenefits.style';
import { ContainerForAllSectionsStyled } from '../ProviderDetailTabs/ProviderDetailTabs.styled';

interface NonUhcVisionProps {
  id: string;
  key: string;
  title: string;
  content: string;
  hyperlink: string;
  cta: string;
  chipValue: string;
  hpbpCode: string;
}
interface Props {
  NonUhcVisionContent: NonUhcVisionProps[];
  isPsxHome?: boolean;
}

interface BoxContentProps {
  nonUhcVisionData: NonUhcVisionProps;
  index: number;
}
export const CardButton = {
  'abyss-button-root': {
    '@media (max-width: 464px)': {
      fontWeight: '600',
    },
  },
};
export const ContentCardsContainer = styled('div', {
  dynamic: ({ cssProps: { backgroundColor } = {} }) => ({
    backgroundColor,
  }),
  height: '100%',
  padding: '$md 24px',
  borderRadius: '8px',
  '@media (max-width: 464px)': {
    padding: '$md 24px',
    paddingLeft: 0,
    minHeight: '174px',
  },
});
export const NonUhcVisionPage = ({ NonUhcVisionContent, isPsxHome }: Props) => {
  const displayCarousel = useMediaQuery(benefitsCarouselScreen);
  const BoxContent = ({ nonUhcVisionData, index }: BoxContentProps) => {
    const { t } = useTranslation();
    const { title, content, hyperlink, cta } = nonUhcVisionData;

    const cardsBackgrounds = {
      0: '$core.color.aqua.15',
      1: '#FCF6F1',
      2: '#EFF9EF',
    };

    const datatestid = title.replace(/[^a-zA-Z ]/g, '').replace(/\s+/g, '-');
    const mobileScreen = useMediaQuery(phoneOnly);

    const { chipValue } = useChipStore(
      useShallow((state: ChipState) => ({
        chipValue: state.chipValue,
      }))
    );

    const { adobePageTrackEvent } = useAdobePageTrackEvent({
      pageName: 'vision overview',
    });

    const hasTrackedVisionEvent = useRef(false);
    useEffect(() => {
      const debouncedTrackEvent = debounce(() => {
        if (!hasTrackedVisionEvent.current && isPsxHome) {
          adobePageTrackEvent();
          hasTrackedVisionEvent.current = true;
        }
      }, 200);

      debouncedTrackEvent();
      return () => {
        debouncedTrackEvent.cancel();
      };
    }, [chipValue]);

    const analyticsInfo = {
      name: cta,
      location: 'body:about vision coverage',
      destinationUrl: hyperlink,
    };
    const handleLinkTrackEvent = () => {
      adobeLinkTrackEvent({
        ...analyticsInfo,
      });

      window.open(hyperlink, '_blank');
    };
    return (
      <ContentCardsContainer
        cssProps={{ backgroundColor: cardsBackgrounds[index % 3] }}
      >
        <Layout.Group
          alignItems="top"
          css={{
            '&, > div': {
              height: '100%',
              width: '384px',
            },
          }}
          data-auto-testid="non-uhc-vision-card"
          data-testid="non-uhc-vision-card"
        >
          <Layout.Stack
            alignItems="left"
            css={{
              'div:nth-of-type(2)': {
                flexGrow: 1,
                paddingBottom: '8px',
              },
            }}
          >
            <Heading
              data-auto-testid={`${datatestid}-heading`}
              data-testid={`${datatestid}-heading`}
              level={3}
              size="xs"
            >
              {t(title)}
            </Heading>
            <Text
              color="$core.color.neutral.90"
              data-auto-testid={`${datatestid}-content`}
              data-testid={`${datatestid}-content`}
              fontSize={mobileScreen ? '$sm' : '$md'}
            >
              {content}{' '}
              <Text as="span" css={{ fontWeight: 'bold' }}>
                United Healthcare.
              </Text>
            </Text>
            <Button
              aria-label={`${cta}`.toLowerCase()}
              css={CardButton}
              data-auto-testid={`${datatestid}-button`}
              data-testid={`${datatestid}-button`}
              icon={{ icon: 'open_in_new', position: 'trailing' }}
              name="get_started"
              onClick={handleLinkTrackEvent}
            >
              <Text color="white" fontSize={mobileScreen ? '$sm' : '$md'}>
                {cta}
              </Text>
            </Button>
          </Layout.Stack>
        </Layout.Group>
      </ContentCardsContainer>
    );
  };

  const slides = NonUhcVisionContent?.map((nonUhcVisionData, i) => (
    <Slide key={nonUhcVisionData.key}>
      <div css={slideContainerStyle}>
        <BoxContent index={i} nonUhcVisionData={nonUhcVisionData} />
      </div>
    </Slide>
  )).filter(Boolean); // filter out null values

  // If there are no slides to show, don't render the Carousel
  if (slides.length === 0) {
    return null;
  }

  return (
    <ContainerForAllSectionsStyled
      data-auto-testid={`my-benefits-section`}
      data-testid={`my-benefits-section`}
      height="auto"
    >
      <ContentWrapper>
        <Carousel
          css={
            (displayCarousel || NonUhcVisionContent.length > 2) &&
            NonUhcVisionContent.length !== 1
              ? carouselStyle
              : {
                  ...carouselStyle,
                  'abyss-carousel-minimal-controls': {
                    display: 'none',
                  },
                }
          }
          key={`benefit-data-${
            NonUhcVisionContent ? NonUhcVisionContent.length : 0
          }`}
          label=""
          slides={slides}
          slidesPerView={1}
        />
      </ContentWrapper>
    </ContainerForAllSectionsStyled>
  );
};
